import { Fragment, useEffect, useId, useState } from "react";
import { useTranslations } from "../../utils/translations";
import "./AcrossChaptersMap.css";

const cityPins = [
  { id: "saoPaulo", lat: -23.55, lon: -46.63 },
  { id: "abidjan", lat: 5.36, lon: -4.01 },
  { id: "frankfurt", lat: 50.11, lon: 8.68 },
  { id: "fulda", lat: 50.56, lon: 9.68 },
  { id: "munich", lat: 48.14, lon: 11.58 },
  { id: "berlin", lat: 52.52, lon: 13.4 },
  { id: "prague", lat: 50.08, lon: 14.44 },
  { id: "budapest", lat: 47.5, lon: 19.04 },
  { id: "mumbai", lat: 19.08, lon: 72.88 },
  { id: "pune", lat: 18.52, lon: 73.86 },
  { id: "newDelhi", lat: 28.61, lon: 77.21 },
  { id: "hyderabad", lat: 17.39, lon: 78.49 },
  { id: "kolkata", lat: 22.57, lon: 88.36 },
  { id: "bangkok", lat: 13.75, lon: 100.52 },
];

const MAP_LON_MIN = -50;
const MAP_LON_MAX = 102;
const curveOffsets = [-1, 0, 1];

const curveRoutes = [
  { from: "saoPaulo", to: "frankfurt", lift: -12, spread: 2.2, tone: "warm" },
  { from: "saoPaulo", to: "newDelhi", lift: -8, spread: 2.6, tone: "cool" },
  { from: "abidjan", to: "frankfurt", lift: -4.2, spread: 1.4, tone: "warm" },
  { from: "berlin", to: "newDelhi", lift: -8, spread: 1.8, tone: "neutral" },
  { from: "berlin", to: "mumbai", lift: -6, spread: 1.7, tone: "cool" },
  { from: "frankfurt", to: "hyderabad", lift: -4, spread: 1.4, tone: "warm" },
  { from: "prague", to: "kolkata", lift: -3, spread: 1.6, tone: "cool" },
  { from: "munich", to: "pune", lift: 2.5, spread: 1.2, tone: "neutral" },
  { from: "berlin", to: "budapest", lift: -3.2, spread: 0.8, tone: "cool" },
  { from: "frankfurt", to: "fulda", lift: 0.8, spread: 0.6, tone: "neutral" },
  { from: "newDelhi", to: "hyderabad", lift: 1.5, spread: 0.8, tone: "warm" },
  { from: "newDelhi", to: "kolkata", lift: -1.8, spread: 0.8, tone: "cool" },
  { from: "newDelhi", to: "mumbai", lift: 1.6, spread: 0.8, tone: "neutral" },
  { from: "newDelhi", to: "pune", lift: 2.1, spread: 0.8, tone: "neutral" },
  { from: "bangkok", to: "kolkata", lift: -2.5, spread: 1.0, tone: "warm" },
  { from: "bangkok", to: "berlin", lift: -9, spread: 2.0, tone: "cool" },
];

// Abstract continent blobs — same coordinate system as city pins:
// x = ((lon + 50) / 150) * 100,  y = ((90 - lat) / 180) * 100
const continentPaths = [
  {
    id: "south-america",
    // Eastern coastal sliver visible within lon range (Sao Paulo sits at ~x2, y63)
    d: "M 0 46 C 9 42 14 48 14 53 C 14 58 12 62 9 66 C 6 69 3 72 0 73 L 0 46 Z",
  },
  {
    id: "africa",
    // Wide in north, narrows to cape in south; Abidjan at ~x31, y47
    d: "M 23 40 C 31 33 43 30 57 33 C 65 36 68 43 66 52 C 63 59 53 68 45 70 C 36 72 26 64 24 53 C 22 48 21 44 23 40 Z",
  },
  {
    id: "europe",
    // Compact mass above Africa; European cities cluster around x39-46, y21-24
    d: "M 27 28 C 33 20 42 13 48 12 C 57 11 66 14 68 17 C 68 22 63 25 57 27 C 51 29 43 29 36 29 C 32 29 28 30 27 28 Z",
  },
  {
    id: "arabia",
    // Small peninsula between Africa and India
    d: "M 57 33 C 63 31 71 33 73 38 C 73 43 68 46 63 46 C 60 46 58 43 57 39 C 55 36 56 34 57 33 Z",
  },
  {
    id: "india",
    // Teardrop peninsula; Indian cities sit at x82-92, y34-40
    d: "M 74 30 C 82 28 92 30 98 33 C 98 37 95 39 93 40 C 91 43 90 46 88 48 C 86 50 85 52 83 58 C 81 62 82 66 82 67 C 79 62 78 57 77 52 C 75 48 73 42 74 30 Z",
  },
  {
    id: "southeast-asia",
    // Indochina peninsula; Bangkok sits at ~x99, y42
    d: "M 93 33 C 98 30 102 34 101 40 C 100 45 98 49 96 51 C 93 53 90 49 91 44 C 91 39 91 35 93 33 Z",
  },
];

const TIMEZONE_CLOCKS = [
  { label: "São Paulo", tz: "America/Sao_Paulo" },
  { label: "Abidjan", tz: "Africa/Abidjan" },
  { label: "MEZ", tz: "Europe/Berlin" },
  { label: "IST", tz: "Asia/Kolkata" },
  { label: "Thailand", tz: "Asia/Bangkok" },
];

const formatTime = (tz) =>
  new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: tz,
  }).format(new Date());


const clampPercent = (value) => Math.min(100, Math.max(0, value));

const projectCityCoordinates = ({ lat, lon }) => ({
  x: clampPercent(((lon - MAP_LON_MIN) / (MAP_LON_MAX - MAP_LON_MIN)) * 100),
  y: clampPercent(((90 - lat) / 180) * 100),
});

const projectCityPin = (city) => {
  const { x, y } = projectCityCoordinates(city);
  return {
    left: `${x}%`,
    top: `${y}%`,
  };
};

const cityCoordinates = cityPins.reduce((accumulator, city) => {
  accumulator[city.id] = projectCityCoordinates(city);
  return accumulator;
}, {});

const buildCurvePath = (start, end, lift = 0, offset = 0) => {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const distance = Math.hypot(deltaX, deltaY) || 1;
  const normalX = -deltaY / distance;
  const normalY = deltaX / distance;
  const middleX = (start.x + end.x) / 2;
  const middleY = (start.y + end.y) / 2;
  const controlX = middleX + normalX * (lift + offset);
  const controlY = middleY + normalY * (lift + offset);

  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} Q ${controlX.toFixed(2)} ${controlY.toFixed(
    2
  )} ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
};

const AcrossChaptersMap = ({ className = "" }) => {
  const { t } = useTranslations();
  const cityLabel = (id) => t("acrossChaptersMap", `cities.${id}`, id);
  const [activeCity, setActiveCity] = useState("");
  const [, setTick] = useState(0);
  const titleId = useId();

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  const rootClassName = ["about-map", className].filter(Boolean).join(" ");

  return (
    <section className={rootClassName} aria-labelledby={titleId}>
      <div className="about-map-head">
        <div className="about-map-title-wrap">
          <span className="about-map-index">{t('acrossChaptersMap','head.index')}</span>
          <h2 id={titleId}>{t('acrossChaptersMap', 'head.title')}</h2>
        </div>
        <div className="about-map-head-right">
          <p>{t('acrossChaptersMap','head.right')}</p>
          <div className="about-map-clocks" aria-label="Local times">
            {TIMEZONE_CLOCKS.map(({ label, tz }, i) => (
              <Fragment key={tz}>
                {i > 0 && <span className="about-map-clock-sep" aria-hidden="true">·</span>}
                <span className="about-map-clock-time" aria-label={`${label} time`}>
                  {formatTime(tz)}
                </span>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
      <div className="about-map-frame" role="group" aria-label="Map with city locations">
        <svg className="about-map-curves" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <g className="about-map-continents">
            {continentPaths.map((continent) => (
              <path key={continent.id} className="about-map-continent" d={continent.d} />
            ))}
          </g>
          {curveRoutes.map((route) => {
            const start = cityCoordinates[route.from];
            const end = cityCoordinates[route.to];
            if (!start || !end) {
              return null;
            }

            return (
              <g key={`${route.from}-${route.to}`}>
                {curveOffsets.map((offset) => (
                  <path
                    key={`${route.from}-${route.to}-${offset}`}
                    className={`about-map-curve about-map-curve-${route.tone}${
                      offset === 0 ? " is-core" : ""
                    }`}
                    d={buildCurvePath(start, end, route.lift, offset * route.spread)}
                  />
                ))}
              </g>
            );
          })}
        </svg>
        {cityPins.map((city) => {
          const position = projectCityPin(city);
          const coordinates = cityCoordinates[city.id];
          const isActive = activeCity === city.id;
          const label = cityLabel(city.id);
          const isEdgeLeft = (coordinates?.x ?? 50) < 10;
          const isEdgeRight = (coordinates?.x ?? 50) > 90;
          return (
            <button
              key={city.id}
              type="button"
              className={`about-map-pin${isActive ? " is-active" : ""}${isEdgeLeft ? " is-edge-left" : ""}${
                isEdgeRight ? " is-edge-right" : ""
              }`}
              style={{
                left: position.left,
                top: position.top,
              }}
              onMouseEnter={() => setActiveCity(city.id)}
              onMouseLeave={() => setActiveCity("")}
              onFocus={() => setActiveCity(city.id)}
              onBlur={() => setActiveCity("")}
              aria-label={label}
            >
              <span className="about-map-dot" aria-hidden="true" />
              <span className="about-map-tooltip">{label}</span>
            </button>
          );
        })}
      </div>
      <ul className="about-map-cities" aria-label="City locations">
        {cityPins.map((city) => {
          const isActive = activeCity === city.id;
          return (
            <li key={`${city.id}-city`}>
              <button
                type="button"
                className={`about-map-city${isActive ? " is-active" : ""}`}
                onMouseEnter={() => setActiveCity(city.id)}
                onMouseLeave={() => setActiveCity("")}
                onFocus={() => setActiveCity(city.id)}
                onBlur={() => setActiveCity("")}
              >
                {cityLabel(city.id)}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default AcrossChaptersMap;
